/**
 * Flow Optimizer — manual rotation preview/apply mutations. (Automatic rotation
 * runs server-side as part of the optimizer's scheduled update — no toggle.)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import {
  applyRotation,
  previewRotation,
} from '../services/flow-optimizer-queue-api';
import { queueKeys } from './useFlowOptimizerQueue';

/** Key of the config query owned by useFlowOptimizerConfig (kept in sync here). */
const configKey = (provider: QueueProvider, campaignId: string) =>
  [provider, 'flow-optimizer', 'config', campaignId] as const;

/** Preview + apply rotation. Apply invalidates the queue so it re-reads state. */
export function useRotation(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const qc = useQueryClient();
  const invalidateQueue = () =>
    qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) });

  const preview = useMutation({
    mutationFn: () => previewRotation(campaignId, provider),
  });

  const apply = useMutation({
    mutationFn: () => applyRotation(campaignId, provider),
    onSuccess: () => {
      void invalidateQueue();
      void qc.invalidateQueries({ queryKey: configKey(provider, campaignId) });
      void qc.invalidateQueries({
        queryKey: queueKeys.rotationHistory(provider, campaignId),
      });
    },
  });

  return { preview, apply };
}
